import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Notifications, Event, Payment, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [ignoredNotifications, setIgnoredNotifications] = useState([]);
  const [swipingItem, setSwipingItem] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Carregar notificações ignoradas do localStorage
    const ignored = localStorage.getItem(`ignored_notifications_${user?.uid}`);
    if (ignored) {
      setIgnoredNotifications(JSON.parse(ignored));
    }
  }, [user?.uid]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const hoje = new Date();
        const proximosDias = new Date(hoje.getTime() + 7 * 24 * 60 * 60 * 1000); // próximos 7 dias
        const notifs = [];

        // Buscar empréstimos próximos a vencer
        const eSnap = await getDocs(collection(db, `users/${user.uid}/emprestimos`));
        eSnap.docs.forEach(doc => {
          const emp = { id: doc.id, ...doc.data() };
          const dataVenc = new Date(emp.dataVencimento);
          if (dataVenc <= proximosDias && emp.status !== 'recebido') {
            const diasAte = Math.ceil((dataVenc - hoje) / (1000 * 60 * 60 * 24));
            notifs.push({
              id: `emp-${emp.id}`,
              type: 'emprestimo',
              title: `Empréstimo: ${emp.pessoa}`,
              message: diasAte <= 0 ? `Vencido há ${Math.abs(diasAte)} dias` : `Vence em ${diasAte} dia(s)`,
              value: emp.valor,
              date: emp.dataVencimento,
              route: '/emprestimos',
              urgent: diasAte <= 0
            });
          }
        });

        // Buscar gastos mensais próximos a vencer
        const gSnap = await getDocs(query(collection(db, `users/${user.uid}/gastos_mensais`), orderBy('dataVencimento', 'asc')));
        gSnap.docs.forEach(doc => {
          const gasto = { id: doc.id, ...doc.data() };
          const dataVenc = new Date(gasto.dataVencimento);
          if (dataVenc <= proximosDias && gasto.status !== 'pago') {
            const diasAte = Math.ceil((dataVenc - hoje) / (1000 * 60 * 60 * 24));
            notifs.push({
              id: `gasto-${gasto.id}`,
              type: 'boleto',
              title: `Boleto: ${gasto.nome}`,
              message: diasAte <= 0 ? `Vencido há ${Math.abs(diasAte)} dias` : `Vence em ${diasAte} dia(s)`,
              value: gasto.valor,
              date: gasto.dataVencimento,
              route: '/gastos',
              urgent: diasAte <= 0
            });
          }
        });

        // Notificações de cobrança
        const nSnap = await getDocs(query(collection(db, `users/${user.uid}/billing_notifications`), orderBy('createdAt', 'desc')));
        nSnap.docs.forEach(doc => {
          const notif = { id: `bill-${doc.id}`, ...doc.data() };
          const created = notif.createdAt?.toDate ? notif.createdAt.toDate() : (notif.createdAt ? new Date(notif.createdAt) : new Date());
          notifs.push({
            id: notif.id,
            type: 'cobranca',
            title: notif.titulo || 'Assinatura',
            message: notif.mensagem || 'Atualize seu pagamento para manter o acesso.',
            date: created,
            route: '/',
            urgent: notif.status === 'novo'
          });
        });

        // Ordenar por urgência e data
        notifs.sort((a, b) => {
          if (a.urgent && !b.urgent) return -1;
          if (!a.urgent && b.urgent) return 1;
          return new Date(a.date) - new Date(b.date);
        });

        // Filtrar notificações ignoradas
        const filteredNotifs = notifs.filter(notif => !ignoredNotifications.includes(notif.id));

        setNotifications(filteredNotifs);
      } catch (error) {
        console.error("Erro ao buscar notificações:", error);
      }
    };

    if (user?.uid) {
      fetchNotifications();
    }
  }, [user, ignoredNotifications]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    navigate(notification.route);
    handleClose();
  };

  const handleDeleteNotification = (event, notificationId) => {
    event.stopPropagation(); // Impedir que o clique no item seja acionado
    const newIgnored = [...ignoredNotifications, notificationId];
    setIgnoredNotifications(newIgnored);
    localStorage.setItem(`ignored_notifications_${user.uid}`, JSON.stringify(newIgnored));
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const handleTouchStart = (event, notification) => {
    setSwipingItem(notification);
    setSwipeOffset(0);
  };

  const handleTouchMove = (event) => {
    if (!swipingItem) return;
    
    const touch = event.touches[0];
    const deltaX = touch.clientX - event.currentTarget.getBoundingClientRect().left;
    const maxSwipe = 100; // Máximo de swipe em pixels
    
    if (deltaX < 0) { // Swipe para esquerda
      setSwipeOffset(Math.max(deltaX, -maxSwipe));
    } else if (deltaX > 0) { // Swipe para direita
      setSwipeOffset(Math.min(deltaX, maxSwipe));
    }
  };

  const handleTouchEnd = (event) => {
    if (!swipingItem) return;
    
    const threshold = 50; // Threshold para considerar como swipe válido
    
    if (Math.abs(swipeOffset) > threshold) {
      // Swipe válido - deletar notificação
      handleDeleteNotification(event, swipingItem.id);
    }
    
    setSwipingItem(null);
    setSwipeOffset(0);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          bgcolor: 'white',
          boxShadow: 2,
          '&:hover': { bgcolor: '#f5f5f5' },
          ml: 1
        }}
      >
        <Badge badgeContent={notifications.length} color="error">
          <Notifications color="action" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 350, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Notificações
          </Typography>
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              Nenhuma notificação pendente
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notif) => (
            <MenuItem
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              onTouchStart={(e) => handleTouchStart(e, notif)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sx={{
                py: 1.5,
                px: 2,
                borderLeft: notif.urgent ? '4px solid #f44336' : '4px solid #ff9800',
                position: 'relative',
                transform: swipingItem?.id === notif.id ? `translateX(${swipeOffset}px)` : 'translateX(0)',
                transition: swipingItem?.id === notif.id ? 'none' : 'transform 0.2s ease',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                {notif.type === 'emprestimo' ? (
                  <Event color={notif.urgent ? 'error' : 'warning'} />
                ) : (
                  <Payment color={notif.urgent ? 'error' : 'warning'} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                    {notif.title}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.8rem' }}>
                      {notif.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      R$ {notif.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                }
              />
              <IconButton
                size="small"
                onClick={(e) => handleDeleteNotification(e, notif.id)}
                sx={{
                  ml: 1,
                  opacity: 0.7,
                  '&:hover': { opacity: 1, bgcolor: 'error.main', color: 'white' }
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}

export default NotificationBell;
